import { useEffect, useRef, useState } from "react";

/**
 * Safety valve: how long the stale pre-change basket stays suppressed when the
 * refresh can never land (a persistent WAF challenge degrades every attempt
 * silently). It is measured from when the invalidation is armed and is NOT
 * extended by a slow read: one stalled read lasts the 8 s SCAPI upstream cap, so
 * after a stall the valve has already disarmed and the stale answer gets no
 * retry — recovery then waits for the next mutation or remount. Fast failures
 * (WAF 403, fast 5xx) do get the 2.5 s backoff retry. Deliberately not
 * re-armed per settle: that would retry into an outage and lengthen the
 * checkout-hop deadline derived from this value. Exported because the post-sign-in checkout hop's never-strand
 * deadline is derived from it — the hop must outlast this window so the
 * out-of-stock gate gets its chance at a freshly merged basket
 * (`use-checkout-hop.ts`). Keep the two ordered, not merely close.
 */
export const BASKET_REFRESH_MAX_AWAIT_MS = 8000;

/**
 * COM-63 — upper bound on reload attempts per invalidation. Each attempt is a
 * fail-soft preflight GET + the keyed `fetcher.load()` (two SCAPI round-trips),
 * so an answer that never turns "fresh" must not spin until the time valve.
 * (COM-840: the preflight is gone, so an attempt is one loader run.)
 */
export const BASKET_REFRESH_MAX_ATTEMPTS = 3;

/**
 * COM-840 — pause before re-reading after a CONTAINED / STALE settle
 * (`fetcherData === null`: the loader answered stale or the clientLoader
 * contained a failure). That answer means Salesforce is failing right now, so
 * an immediate retry only adds a full loader run to the outage. Cleared on
 * unmount and when the await ends.
 */
export const BASKET_REFRESH_RETRY_DELAY_MS = 2500;

/**
 * Basket-invalidation refresh that SURVIVES navigation (SFCC-11878 follow-up,
 * generalised for SFCC-11869).
 *
 * Two events change the basket behind the client's back, and both leave the
 * shared keyed "basket" fetcher holding a stale snapshot: a basket MUTATION, and
 * an AUTH FLIP (the /login action merges the guest basket into the registered one
 * server-side). Same problem, different triggers, so they share one mechanism —
 * `mutationInFlight` for the former, a change in `invalidatedBy` for the latter.
 *
 * Basket mutations POST on component-scoped anonymous fetchers
 * (`useBasketActions`), so the shared keyed "basket" loader fetcher only
 * refreshes via a trailing `fetcher.load()`. That trailing load used to be
 * fired from an effect inside the MUTATING component — navigate right after an
 * add-to-cart ("View Cart" in the ATC flyout) and it was lost twice over: the
 * owner unmounts before its effect fires, or the in-flight keyed load is
 * canceled by the navigation. The cart page then derived from the mount-time
 * `softBasket` snapshot (fetched BEFORE the add) and rendered a stale
 * "Your cart is empty" until a manual refresh.
 *
 * This hook is called from `BasketProvider` (root-mounted, never unmounts), so
 * it owns the refresh instead: when a basket mutation POST is observed — or
 * `invalidatedBy` changes — stay "awaiting" until the keyed fetcher receives a
 * loader-shaped payload minted AFTER the change, re-firing `reload` each time a
 * previous attempt was lost (nav-cancel) or skipped (WAF challenge on the
 * fail-soft preflight, SFCC-11929). Returns the `awaiting` flag so the caller can
 * suppress its stale pre-change fallback data (=> loading state) while the
 * refresh is pending.
 *
 * "Fresh" = `fetcherData` is non-null, loader-shaped (`products` key present —
 * action responses don't carry it), and a DIFFERENT identity from the snapshot
 * taken when the invalidation was observed.
 */
export function useBasketInvalidationRefresh({
  mutationInFlight,
  invalidatedBy,
  fetcherState,
  fetcherData,
  reload,
  /** Override the safety valve; defaults to `BASKET_REFRESH_MAX_AWAIT_MS`. */
  maxAwaitMs = BASKET_REFRESH_MAX_AWAIT_MS,
  retryDelayMs = BASKET_REFRESH_RETRY_DELAY_MS,
}: {
  mutationInFlight: boolean;
  /**
   * Any value whose change means the SERVER-side basket changed — currently the
   * auth identity, whose flip runs the guest->registered merge inside /login.
   * Compared by identity; the first render only records it, so mounting never
   * counts as an invalidation.
   */
  invalidatedBy?: unknown;
  fetcherState: "idle" | "loading" | "submitting";
  fetcherData: unknown;
  reload: () => void;
  maxAwaitMs?: number;
  retryDelayMs?: number;
}): boolean {
  const [awaiting, setAwaiting] = useState(false);
  // `fetcherData` snapshot taken when the invalidation was observed.
  const preMutationDataRef = useRef<unknown>(null);
  // Last seen `invalidatedBy`, seeded on the first render so a mount is never
  // mistaken for a change — which is why the auth path needs no mount-skip ref
  // of its own.
  const lastInvalidatorRef = useRef<unknown>(invalidatedBy);
  // Keep the latest reload callback without making the effect depend on its
  // (unstable, per-render) identity — re-firing on every render would flood
  // the loader (see feedback_usefetcher_unstable_object_deps_loop).
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  // Reload attempts fired for the CURRENT invalidation (reset on arm).
  const attemptsRef = useRef(0);
  // True between our `reload()` call and the loader state leaving idle, so a
  // non-idle state we did NOT start (React Router's post-action revalidation of
  // an already-loaded keyed fetcher) can be told apart and counted.
  const ownLoadPendingRef = useRef(false);

  useEffect(() => {
    const invalidated = lastInvalidatorRef.current !== invalidatedBy;
    lastInvalidatorRef.current = invalidatedBy;
    if (mutationInFlight || invalidated) {
      preMutationDataRef.current = fetcherData;
      attemptsRef.current = 0;
      ownLoadPendingRef.current = false;
      setAwaiting(true);
      return;
    }
    if (!awaiting) return;
    if (fetcherState !== "idle") {
      // A load is in flight — let it land. COM-840: if we did not start it
      // (post-action revalidation), count it as an attempt so a stale/null
      // settle takes the backoff branch instead of an immediate second run.
      if (ownLoadPendingRef.current) ownLoadPendingRef.current = false;
      else if (
        attemptsRef.current === 0 &&
        preMutationDataRef.current !== undefined // only an already-loaded fetcher (basket OR a held null) revalidates
      ) {
        attemptsRef.current = 1;
      }
      return;
    }
    const fresh =
      fetcherData != null &&
      fetcherData !== preMutationDataRef.current &&
      typeof fetcherData === "object" &&
      "products" in fetcherData;
    if (fresh) {
      setAwaiting(false);
      return;
    }
    // COM-63 — a contained `clientLoader` failure resolves the keyed fetcher
    // to `null`. It counts as an ordinary non-fresh settle: retried against
    // the attempts cap below (a single transient blip after an ATC must not
    // strand the header on the pre-mutation payload), but never past it —
    // each attempt is two SCAPI calls, and the loader beacons once per scope,
    // so cost and noise stay bounded.
    if (attemptsRef.current >= BASKET_REFRESH_MAX_ATTEMPTS) {
      setAwaiting(false);
      return;
    }
    // First attempt, or a previous attempt was lost (nav-cancel) / skipped
    // (WAF challenge on the preflight). Each fetcher state settle re-enters
    // this effect until a fresh payload lands, the attempts cap, or the
    // safety valve below trips.
    // COM-840 — a keyed fetcher that is ALREADY loaded is revalidated by React
    // Router right after the action, inside the mutation fetcher's own busy
    // window, so this effect never sees it go non-idle. Its signature is the
    // payload identity changing under us (stale answer -> contained `null`) with
    // no load of ours behind it: count that run as the attempt. A fetcher that
    // already held `null` is treated the same (the revalidation cannot be seen).
    if (
      attemptsRef.current === 0 &&
      !ownLoadPendingRef.current &&
      fetcherData === null &&
      // `undefined` = never loaded (no revalidation). A `null` held from before
      // the mutation counts too: RR7 revalidates that loaded fetcher as well,
      // and null -> null is invisible by identity, so without this a second add
      // during a stall paid that revalidation plus an immediate reload.
      preMutationDataRef.current !== undefined
    ) {
      attemptsRef.current = 1;
    }
    if (attemptsRef.current >= 1 && fetcherData === null) {
      // COM-840 — a previous attempt settled contained/stale: back off rather
      // than spend the next loader run back-to-back during an outage.
      const retryTimer = setTimeout(() => {
        attemptsRef.current += 1;
        ownLoadPendingRef.current = true;
        reloadRef.current();
      }, retryDelayMs);
      return () => clearTimeout(retryTimer);
    }
    attemptsRef.current += 1;
    ownLoadPendingRef.current = true;
    reloadRef.current();
  }, [
    mutationInFlight,
    invalidatedBy,
    awaiting,
    fetcherState,
    fetcherData,
    retryDelayMs,
  ]);

  useEffect(() => {
    if (!awaiting) return;
    const timer = setTimeout(() => setAwaiting(false), maxAwaitMs);
    return () => clearTimeout(timer);
  }, [awaiting, maxAwaitMs]);

  return awaiting;
}
